// Analytics/services/endpoints/outpatients.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
// Enable/disable mocking globally or per endpoint
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_OUTPATIENTS === 'true', // Set to 'true' in .env to enable mocking
    endpoints: {
        '': true,                          // stats (base)
        'patients': true,                  // getStatsOutpatients
        'department': true,                // getOutpatientsByDepartment
        'diagnosis': true,                 // getDiagnosisWithAppointments
        'appointment-distribution': true,   // getAppointments
        'total-patients': true,            // getTotalPatients
        'new-patients-registration': true, // getNewPatientsRegistration
        'overdue-appointments': true,      // getOverdueAppointments
        'appointments-by-doctor': true,    // getAppointmentsByDoctor
        'appointment-stats': true,         // getAppointmentStats
        'appointment-type': true,          // getAppointmentType
        'activity': true,                  // getActivity
        'appointment-wait-time': true,     // getWaitTime
        'appointments-by-insurance': true,  // getAppointmentsByInsurance
    },
    delay: 300 // Network delay simulation in ms
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

/**
 * Mock response for stats/patients endpoint
 * Returns OutPatientResponse format
 */
const getMockStatsResponse = () => ({
    status: 200,
    trend: {
        type: "months",
        totals: [120, 135, 142, 128, 150, 145, 160],
        data: [120, 135, 142, 128, 150, 145, 160]
    },
    stats: {
        "dept-uuid-1": { name: "Cardiology", totalPatients: 45 },
        "dept-uuid-2": { name: "Neurology", totalPatients: 32 },
        "dept-uuid-3": { name: "Orthopedics", totalPatients: 28 }
    },
    query: [
        { date: new Date('2024-01-01'), amounts: [120] },
        { date: new Date('2024-02-01'), amounts: [135] },
        { date: new Date('2024-03-01'), amounts: [142] }
    ]
});

/**
 * Mock response for diagnosis endpoint
 * Returns DiagnosisResponse format
 */
const getMockDiagnosisResponse = () => ({
    status: 200,
    stats: {
        "I10": {
            name: "Essential hypertension",
            patientsLength: 45,
            amount: 4500,
            patientIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45]
        },
        "E11": {
            name: "Type 2 diabetes mellitus",
            patientsLength: 32,
            amount: 3200,
            patientIds: [46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77]
        },
        "J44": {
            name: "Chronic obstructive pulmonary disease",
            patientsLength: 28,
            amount: 2800,
            patientIds: [78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105]
        }
    },
    department: "all",
    patients: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105]
});

/**
 * Mock response for appointment-distribution endpoint
 * Returns DistributionResponse format
 */
const getMockAppointmentDistributionResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Scheduled", data: [45, 52, 48, 61, 55, 48, 67] },
            { name: "Completed", data: [40, 48, 45, 55, 50, 45, 60] },
            { name: "Cancelled", data: [5, 4, 3, 6, 5, 3, 7] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 500
    },
    stats: {
        "dept-uuid-1": 150,
        "dept-uuid-2": 120,
        "dept-uuid-3": 100
    }
});

/**
 * Mock response for total-patients endpoint
 * Returns ChartDataResponse format
 */
const getMockTotalPatientsResponse = () => ({
    status: 200,
    props: {
        series: [
            { name: "Cardiology", data: [45, 52, 38, 61, 55, 48, 67] },
            { name: "Neurology", data: [32, 28, 35, 42, 38, 31, 40] },
            { name: "Orthopedics", data: [28, 33, 29, 36, 31, 27, 34] },
            { name: "Pediatrics", data: [22, 25, 20, 28, 24, 21, 26] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 234
    }
});

/**
 * Mock response for new-patients-registration endpoint
 * Returns NewPatientStats format (wrapped in status)
 */
const getMockNewPatientsResponse = () => ({
    status: 200,
    stats: {
        "dept-uuid-1": { name: "Cardiology", newPatients: 12 },
        "dept-uuid-2": { name: "Neurology", newPatients: 8 },
        "dept-uuid-3": { name: "Orthopedics", newPatients: 6 },
        "dept-uuid-4": { name: "Pediatrics", newPatients: 10 }
    }
});

/**
 * Mock response for overdue-appointments endpoint
 * Returns AppointmentDepartmentResponse format
 */
const getMockOverdueAppointmentsResponse = () => ({
    status: 200,
    stats: {
        appointments: [
            { name: "Cardiology", totalPatients: 45 },
            { name: "Neurology", totalPatients: 32 }
        ],
        departmentStats: {
            "dept-uuid-1": { name: "Cardiology", count: 15, percentage: "33.3%" },
            "dept-uuid-2": { name: "Neurology", count: 8, percentage: "25.0%" }
        },
        totalOverduePercentage: "29.2%",
        totalAppointments: 77,
        totalOverdue: 23
    }
});

/**
 * Mock response for appointments-by-doctor endpoint
 * Returns format expected by ProductivityTable
 */
const getMockAppointmentsByDoctorResponse = () => ({
    status: 200,
    data: [
        {
            researcher_uuid: "doctor-uuid-1",
            doctor_name: "John",
            doctor_surnames: "Smith",
            department_name: "Cardiology",
            total_appointments: 45,
            unique_patients: 38,
            first_visits: 12,
            follow_ups: 30,
            free_consultation: 3
        },
        {
            researcher_uuid: "doctor-uuid-2",
            doctor_name: "Sarah",
            doctor_surnames: "Jones",
            department_name: "Neurology",
            total_appointments: 38,
            unique_patients: 32,
            first_visits: 10,
            follow_ups: 25,
            free_consultation: 3
        },
        {
            researcher_uuid: "doctor-uuid-3",
            doctor_name: "Michael",
            doctor_surnames: "Williams",
            department_name: "Orthopedics",
            total_appointments: 32,
            unique_patients: 28,
            first_visits: 8,
            follow_ups: 22,
            free_consultation: 2
        },
        {
            researcher_uuid: "doctor-uuid-4",
            doctor_name: "Emily",
            doctor_surnames: "Brown",
            department_name: "Pediatrics",
            total_appointments: 28,
            unique_patients: 24,
            first_visits: 7,
            follow_ups: 19,
            free_consultation: 2
        }
    ],
    stats: {
        totalDoctors: 4,
        totalAppointments: 143,
        totalUniquePatients: 122
    },
    department: "all"
});

/**
 * Mock response for appointment-stats endpoint
 * Returns OutPatientResponse format
 */
const getMockAppointmentStatsResponse = () => ({
    status: 200,
    trend: {
        type: "months",
        totals: [200, 220, 240, 230, 250, 245, 260],
        data: [200, 220, 240, 230, 250, 245, 260]
    },
    stats: {
        "dept-uuid-1": { name: "Cardiology", totalPatients: 150 },
        "dept-uuid-2": { name: "Neurology", totalPatients: 110 }
    }
});

/**
 * Mock response for appointment-type endpoint
 * Returns IAppointmentTypeDataResponse format
 */
const getMockAppointmentTypeResponse = () => ({
    status: 200,
    stats: [
        {
            appointmentType: "Consultation",
            departmentUuid: "dept-uuid-1",
            departmentName: "Cardiology",
            totalAppointments: 45,
            avgDaysUntilAppointment: 7,
            minDaysUntilAppointment: 1,
            maxDaysUntilAppointment: 30,
            mostCommonBookingMonth: "January",
            mostCommonAppointmentMonth: "February",
            mostCommonBookingDay: 1,
            mostCommonAppointmentDay: 8
        },
        {
            appointmentType: "Follow-up",
            departmentUuid: "dept-uuid-1",
            departmentName: "Cardiology",
            totalAppointments: 32,
            avgDaysUntilAppointment: 14,
            minDaysUntilAppointment: 7,
            maxDaysUntilAppointment: 60,
            mostCommonBookingMonth: "January",
            mostCommonAppointmentMonth: "February",
            mostCommonBookingDay: 1,
            mostCommonAppointmentDay: 15
        },
        {
            appointmentType: "Emergency",
            departmentUuid: "dept-uuid-2",
            departmentName: "Neurology",
            totalAppointments: 18,
            avgDaysUntilAppointment: 0,
            minDaysUntilAppointment: 0,
            maxDaysUntilAppointment: 1,
            mostCommonBookingMonth: "January",
            mostCommonAppointmentMonth: "January",
            mostCommonBookingDay: 1,
            mostCommonAppointmentDay: 1
        }
    ]
});

/**
 * Mock response for activity endpoint
 * Returns ActivityPatientsDistributionResponse format
 */
const getMockActivityResponse = () => ({
    status: 200,
    stats: {
        "2024-01-01": [
            { idPatientInvestigation: 1, department: { id: 1, uuid: "dept-uuid-1", code: "CARD", name: "Cardiology", type: 1, createdAt: "2024-01-01", updatedAt: "2024-01-01", deletedAt: null } },
            { idPatientInvestigation: 2, department: { id: 1, uuid: "dept-uuid-1", code: "CARD", name: "Cardiology", type: 1, createdAt: "2024-01-01", updatedAt: "2024-01-01", deletedAt: null } }
        ],
        "2024-01-02": [
            { idPatientInvestigation: 3, department: { id: 2, uuid: "dept-uuid-2", code: "NEURO", name: "Neurology", type: 1, createdAt: "2024-01-01", updatedAt: "2024-01-01", deletedAt: null } }
        ],
        "2024-01-03": [
            { idPatientInvestigation: 4, department: { id: 3, uuid: "dept-uuid-3", code: "ORTHO", name: "Orthopedics", type: 1, createdAt: "2024-01-01", updatedAt: "2024-01-01", deletedAt: null } },
            { idPatientInvestigation: 5, department: { id: 3, uuid: "dept-uuid-3", code: "ORTHO", name: "Orthopedics", type: 1, createdAt: "2024-01-01", updatedAt: "2024-01-01", deletedAt: null } }
        ]
    }
});

/**
 * Mock response for appointment-wait-time endpoint
 * Returns IAppointmentWaitTimeStats format
 */
const getMockWaitTimeResponse = () => ({
    status: 200,
    waitTimeStats: [
        {
            appointmentType: "Consultation",
            departmentUuid: "dept-uuid-1",
            departmentName: "Cardiology",
            totalAppointments: 45,
            avgWaitTime: 7,
            minWaitTime: 1,
            maxWaitTime: 30,
            medianWaitTime: 6,
            percentile90WaitTime: 14,
            waitTimeDistribution: {
                withinWeek: 10,
                withinTwoWeeks: 15,
                withinMonth: 12,
                overMonth: 8
            },
            waitTimeDistributionPercentage: {
                withinWeek: 22.2,
                withinTwoWeeks: 33.3,
                withinMonth: 26.7,
                overMonth: 17.8
            }
        },
        {
            appointmentType: "Follow-up",
            departmentUuid: "dept-uuid-1",
            departmentName: "Cardiology",
            totalAppointments: 32,
            avgWaitTime: 14,
            minWaitTime: 7,
            maxWaitTime: 60,
            medianWaitTime: 12,
            percentile90WaitTime: 28,
            waitTimeDistribution: {
                withinWeek: 8,
                withinTwoWeeks: 12,
                withinMonth: 8,
                overMonth: 4
            },
            waitTimeDistributionPercentage: {
                withinWeek: 25.0,
                withinTwoWeeks: 37.5,
                withinMonth: 25.0,
                overMonth: 12.5
            }
        },
        {
            appointmentType: "Consultation",
            departmentUuid: "dept-uuid-2",
            departmentName: "Neurology",
            totalAppointments: 28,
            avgWaitTime: 10,
            minWaitTime: 2,
            maxWaitTime: 35,
            medianWaitTime: 9,
            percentile90WaitTime: 20,
            waitTimeDistribution: {
                withinWeek: 6,
                withinTwoWeeks: 10,
                withinMonth: 8,
                overMonth: 4
            },
            waitTimeDistributionPercentage: {
                withinWeek: 21.4,
                withinTwoWeeks: 35.7,
                withinMonth: 28.6,
                overMonth: 14.3
            }
        },
        {
            appointmentType: "Emergency",
            departmentUuid: "dept-uuid-2",
            departmentName: "Neurology",
            totalAppointments: 18,
            avgWaitTime: 0,
            minWaitTime: 0,
            maxWaitTime: 1,
            medianWaitTime: 0,
            percentile90WaitTime: 1,
            waitTimeDistribution: {
                withinWeek: 18,
                withinTwoWeeks: 0,
                withinMonth: 0,
                overMonth: 0
            },
            waitTimeDistributionPercentage: {
                withinWeek: 100.0,
                withinTwoWeeks: 0,
                withinMonth: 0,
                overMonth: 0
            }
        }
    ]
});

/**
 * Mock response for appointments-by-insurance endpoint
 * Returns ChartDataResponse format
 */
const getMockAppointmentsByInsuranceResponse = () => ({
    status: 200,
    props: {
        series: [
            { name: "Private Insurance", data: [120] },
            { name: "Public Insurance", data: [80] },
            { name: "Self-Pay", data: [30] }
        ],
        categories: ["Insurance Type"],
        count: 230
    }
});

// ============================================
// MOCK REGISTRY - Maps endpoint paths to mock functions
// ============================================
const MOCK_REGISTRY = {
    '': getMockStatsResponse,
    'patients': getMockStatsResponse,
    'department': getMockStatsResponse,
    'diagnosis': getMockDiagnosisResponse,
    'appointment-distribution': getMockAppointmentDistributionResponse,
    'total-patients': getMockTotalPatientsResponse,
    'new-patients-registration': getMockNewPatientsResponse,
    'overdue-appointments': getMockOverdueAppointmentsResponse,
    'appointments-by-doctor': getMockAppointmentsByDoctorResponse,
    'appointment-stats': getMockAppointmentStatsResponse,
    'appointment-type': getMockAppointmentTypeResponse,
    'activity': getMockActivityResponse,
    'appointment-wait-time': getMockWaitTimeResponse,
    'appointments-by-insurance': getMockAppointmentsByInsuranceResponse,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if mocking is enabled for a specific endpoint
 */
const shouldMockEndpoint = (endpointPath) => {
    if (!MOCK_CONFIG.enabled) return false;
    return MOCK_CONFIG.endpoints[endpointPath] !== false; // Default to true if not explicitly disabled
};

/**
 * Get mock response for an endpoint
 */
const getMockResponse = (endpointPath) => {
    const mockFunction = MOCK_REGISTRY[endpointPath];
    if (!mockFunction) {
        console.warn(`⚠️ No mock function found for endpoint: ${endpointPath}`);
        return null;
    }
    return mockFunction();
};

// ============================================
// REQUEST HANDLING
// ============================================

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

/**
 * Creates full URL for outpatients endpoints
 * @param {string} endpoint - The specific endpoint path (e.g., '/diagnosis')
 * @param {Object} params - Request parameters
 * @returns {string} Complete URL
 */
const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/outpatients`;
    const endpointPath = endpoint || ''; // Handle empty endpoint for base stats
    console.log('Building URL:', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

// Cancellation goes through axios's own CancelToken, NOT an AbortSignal. The pinned
// axios (0.21.1) predates AbortController: `signal` is absent from its request config
// type and its xhr adapter never reads one (`grep -c signal
// node_modules/axios/lib/adapters/xhr.js` → 0), so a signal passed here would be
// silently dropped and cancel nothing. `cancelToken` is the mechanism this version
// actually honours — the adapter calls `request.abort()` off it. These files are .js,
// so nothing type-checks the difference; see SKIN-263 (and SKIN-260 next door).
//
// A cancelled request rejects with a `Cancel`, not an error; callers must branch on
// `axios.isCancel` so a supersession does not surface as a failed load.
const makeOutpatientsRequest = async (params, cancelToken) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Check if we should use mock data
    if (shouldMockEndpoint(endpointPath)) {
        const mockResponse = getMockResponse(endpointPath);
        if (mockResponse) {
            console.log(`🎭 Using mock data for endpoint: ${endpointPath}`);
            await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
            return mockResponse;
        }
    }
    
    // Make real API call
    try {
        const response = await axios.get(url, {
            ...getAuthHeaders(),
            cancelToken
        });
        if (response.status === 200) return response.data;
        console.log('Outpatients API Response:', response.data);
        throw response.data;
    } catch (error) {
        throw error;
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate, cancelToken) =>
        makeOutpatientsRequest({
            endpointPath,
            uuidInvestigation,
            uuidDepartment,
            startDate,
            endDate
        }, cancelToken)
    );
};

// Grouped services object (new way)
export const outpatientServices = {
    getStats: createEndpoint(''),
    getStatsOutpatients: createEndpoint('patients'),
    getOutpatientsByDepartment: createEndpoint('department'),
    getByDepartment: createEndpoint('department'),
    getDiagnosisWithAppointments: createEndpoint('diagnosis'),
    getAppointments: createEndpoint('appointment-distribution'),
    getTotalPatients: createEndpoint('total-patients'),
    getNewPatientsRegistration: createEndpoint('new-patients-registration'),
    getOverdueAppointments: createEndpoint('overdue-appointments'),
    getAppointmentsByDoctor: createEndpoint('appointments-by-doctor'),
    getAppointmentStats: createEndpoint('appointment-stats'),
    getAppointmentType: createEndpoint('appointment-type'),
    getActivity: createEndpoint('activity'),
    getWaitTime: createEndpoint('appointment-wait-time'),
    getAppointmentsByInsurance: createEndpoint('appointments-by-insurance'),
};

// Individual exports for backward compatibility (old way)
export const getStatsOutpatients = createEndpoint('patients');
export const getOutpatientsByDepartment = createEndpoint('department');
export const getDiagnosisWithAppointments = createEndpoint('diagnosis');
export const getAppointments = createEndpoint('appointment-distribution');
export const getTotalPatients = createEndpoint('total-patients');
export const getNewPatientsRegistration = createEndpoint('new-patients-registration');
export const getOverdueAppointments = createEndpoint('overdue-appointments');
export const getAppointmentsByDoctor = createEndpoint('appointments-by-doctor');
export const getAppointmentStats = createEndpoint('appointment-stats');
export const getAppointmentType = createEndpoint('appointment-type');
export const getActivity = createEndpoint('activity');
export const getWaitTime = createEndpoint('appointment-wait-time');
export const getAppointmentsByInsurance = createEndpoint('appointments-by-insurance');

export const OutpatientEndpoints = {
    STATS: '/patients',
    BY_DEPARTMENT: '/department',
    DIAGNOSIS: '/diagnosis',
    APPOINTMENTS: '/appointments',
    TOTAL_PATIENTS: '/total-patients',
    NEW_PATIENTS_REGISTRATION: '/new-patients-registration',
    OVERDUE_APPOINTMENTS: '/overdue-appointments',
    APPOINTMENTS_BY_DOCTOR: '/appointments-by-doctor',
    APPOINTMENT_STATS: '/appointment-stats',
    APPOINTMENT_TYPE: '/appointment-type',
    ACTIVITY: '/activity',
    APPOINTMENTS_BY_INSURANCE: '/appointments-by-insurance'
};
